import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import { IconComponent, useApiHolder } from '@backstage/core-plugin-api';
import { docFeedbackApiRef } from '@internal/plugin-doc-feedback';
import { makeStyles, Theme } from '@material-ui/core';
import FeedbackIcon from '@material-ui/icons/Feedback';
import { ComponentProps, createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/** Fixed alert red, so the badge reads the same in every DevHub theme and in dark mode. */
export const SIDEBAR_BADGE_RED = '#E53935';

const POLL_INTERVAL_MS = 60_000;

/** Text shown in a count badge: whole numbers, capped at "99+". */
export function formatBadgeCount(count: number): string {
  const value = Math.max(0, Math.floor(count));
  return value > 99 ? '99+' : String(value);
}

const pill = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box' as const,
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 9,
  backgroundColor: SIDEBAR_BADGE_RED,
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums' as const,
};

const useStyles = makeStyles((theme: Theme) => ({
  badge: pill,
  iconWrapper: {
    position: 'relative',
    display: 'inline-flex',
  },
  iconBadge: {
    ...pill,
    position: 'absolute',
    top: -7,
    right: -9,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
    borderRadius: 8,
    fontSize: 10,
    boxShadow: `0 0 0 2px ${theme.palette.navigation.background}`,
    pointerEvents: 'none',
  },
}));

/** Red count pill shown on the right of an open sidebar item. Renders nothing for 0. */
export const SidebarCountBadge = ({ count, label }: { count: number; label: string }) => {
  const classes = useStyles();
  if (count <= 0) {
    return null;
  }
  const text = formatBadgeCount(count);
  return (
    <span className={classes.badge} role="status" aria-label={`${text} ${label}`}>
      {text}
    </span>
  );
};

const CountContext = createContext<{ count: number; label: string }>({ count: 0, label: '' });

/**
 * Wraps a sidebar icon so that, while the sidebar is collapsed, the count from
 * the surrounding CountContext is shown on the icon itself.
 */
function withCollapsedCountBadge(Icon: IconComponent): IconComponent {
  const BadgedIcon = (props: ComponentProps<IconComponent>) => {
    const classes = useStyles();
    const { isOpen } = useSidebarOpenState();
    const { count, label } = useContext(CountContext);
    const text = formatBadgeCount(count);
    return (
      <span className={classes.iconWrapper}>
        <Icon {...props} />
        {!isOpen && count > 0 && (
          <span className={classes.iconBadge} role="status" aria-label={`${text} ${label}`}>
            {text}
          </span>
        )}
      </span>
    );
  };
  return BadgedIcon;
}

const BadgedFeedbackIcon = withCollapsedCountBadge(FeedbackIcon);

/**
 * Polls `load` (and reloads whenever `deps` change). Errors and missing APIs
 * count as 0: the badge is a hint and must never break the sidebar.
 */
function usePolledCount(load: (() => Promise<number>) | undefined, deps: unknown[]): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!load) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;
    const refresh = () => {
      load().then(
        value => !cancelled && setCount(Number.isFinite(value) ? value : 0),
        () => !cancelled && setCount(0),
      );
    };
    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return count;
}

/** New open feedback on the user's docs since they last opened Doc Feedback. */
export function useDocFeedbackUnseenCount(): number {
  const docFeedbackApi = useApiHolder().get(docFeedbackApiRef);
  const { pathname } = useLocation();
  const onFeedbackPage = /^\/feedback(\/|$)/.test(pathname);
  const count = usePolledCount(
    docFeedbackApi && !onFeedbackPage
      ? () => docFeedbackApi.getUnseenCount().then(result => result.count)
      : undefined,
    [docFeedbackApi, onFeedbackPage, pathname],
  );
  return onFeedbackPage ? 0 : count;
}

/** "Doc Feedback" sidebar item with a red count of new feedback. */
export const DocFeedbackSidebarItem = () => {
  const count = useDocFeedbackUnseenCount();
  const label = 'new doc feedback';
  return (
    <CountContext.Provider value={{ count, label }}>
      <SidebarItem icon={BadgedFeedbackIcon} to="feedback" text="Doc Feedback">
        <SidebarCountBadge count={count} label={label} />
      </SidebarItem>
    </CountContext.Provider>
  );
};
